import {useState, useMemo, useCallback, type MouseEvent as ReactMouseEvent} from 'react'
import {Button, Menu, MenuItem, IconButton, Tooltip, Box, Divider, ListItemIcon, ListItemText} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckIcon from '@mui/icons-material/Check'
import EditRounded from '@mui/icons-material/EditRounded'
import CloseRounded from '@mui/icons-material/CloseRounded'
import AddRounded from '@mui/icons-material/AddRounded'
import {useTranslation} from 'react-i18next'
import {useAppDispatch, useAppSelector} from '../../../common/store'
import {
  setActiveScene,
  selectActiveSceneIndex,
  selectSceneCount,
  selectSceneNames,
} from '../../../common/components/store/scenesSlice'
import {switchSceneHistory} from '../../../common/undoable'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'
import useSceneDialogs from './useSceneDialogs'

// ── MobileSceneSelect — dropdown menu for scene switching on mobile ──

const MobileSceneSelect = () => {
  const dispatch = useAppDispatch()
  const tokens = useAppTokens()
  const {t} = useTranslation()
  const activeIndex = useAppSelector(selectActiveSceneIndex)
  const sceneCount = useAppSelector(selectSceneCount)
  const sceneNames = useAppSelector(selectSceneNames)
  const {openAdd, openRename, openRemove, dialogs} = useSceneDialogs()

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  const handleOpen = useCallback((e: ReactMouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleSelectScene = useCallback(
    (idx: number) => {
      if (idx !== activeIndex) {
        dispatch(switchSceneHistory({fromScene: activeIndex, toScene: idx}))
        dispatch(setActiveScene(idx))
      }
      handleClose()
    },
    [dispatch, activeIndex, handleClose],
  )

  const handleRenameClick = useCallback(
    (e: ReactMouseEvent, idx: number) => {
      e.stopPropagation()
      handleClose()
      openRename(idx)
    },
    [handleClose, openRename],
  )

  const handleRemoveClick = useCallback(
    (e: ReactMouseEvent, idx: number) => {
      e.stopPropagation()
      handleClose()
      openRemove(idx)
    },
    [handleClose, openRemove],
  )

  const handleAddClick = useCallback(() => {
    handleClose()
    openAdd()
  }, [handleClose, openAdd])

  const sx = useMemo(() => buildStyles(tokens), [tokens])
  const activeName = sceneNames[activeIndex] ?? `Scene ${activeIndex + 1}`

  return (
    <>
      <Button size="small" onClick={handleOpen} endIcon={<ExpandMoreIcon sx={sx.chevron} />} sx={sx.button}>
        <Box component="span" sx={sx.buttonLabel}>
          {activeName}
        </Box>
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose} slotProps={{paper: {sx: sx.menuPaper}}}>
        {Array.from({length: sceneCount}, (_, idx) => {
          const name = sceneNames[idx] ?? `Scene ${idx + 1}`
          const isActive = idx === activeIndex
          return (
            <MenuItem key={idx} onClick={() => handleSelectScene(idx)} sx={sx.menuItem}>
              <ListItemIcon sx={sx.checkIcon}>{isActive && <CheckIcon fontSize="small" />}</ListItemIcon>
              <ListItemText primary={name} slotProps={{primary: {sx: sx.itemText}}} />
              <Tooltip title={t('toolbar.renameScene')} placement="left">
                <IconButton
                  size="small"
                  onClick={(e) => handleRenameClick(e, idx)}
                  sx={sx.actionBtn}
                  aria-label={`${t('toolbar.renameScene')} ${name}`}
                >
                  <EditRounded sx={sx.actionIcon} />
                </IconButton>
              </Tooltip>
              {sceneCount > 1 && (
                <Tooltip title={t('toolbar.removeScene')} placement="left">
                  <IconButton
                    size="small"
                    onClick={(e) => handleRemoveClick(e, idx)}
                    sx={sx.removeBtn}
                    aria-label={`${t('toolbar.removeScene')} ${name}`}
                  >
                    <CloseRounded sx={sx.actionIcon} />
                  </IconButton>
                </Tooltip>
              )}
            </MenuItem>
          )
        })}
        <Divider sx={sx.divider} />
        <MenuItem onClick={handleAddClick} sx={sx.menuItem}>
          <ListItemIcon sx={sx.checkIcon}>
            <AddRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('toolbar.addScene')} slotProps={{primary: {sx: sx.itemText}}} />
        </MenuItem>
      </Menu>
      {dialogs}
    </>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  button: {
    color: tokens.text.primary,
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    textTransform: 'none' as const,
    fontSize: 13,
    fontWeight: 600,
    padding: '4px 8px',
    minWidth: 0,
    maxWidth: 180,
    borderRadius: '6px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  buttonLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  chevron: {
    fontSize: 18,
    color: tokens.text.muted,
  },
  menuPaper: {
    minWidth: 220,
    backgroundColor: tokens.sidebar.background,
    backgroundImage: 'none',
    border: `1px solid ${tokens.accordion.border}`,
  },
  menuItem: {
    fontSize: 13,
    paddingY: '8px',
  },
  itemText: {
    fontSize: 13,
    fontWeight: 500,
    color: tokens.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  checkIcon: {
    minWidth: '28px !important',
    color: tokens.accent.primary,
  },
  actionBtn: {
    padding: '4px',
    marginLeft: '4px',
    color: tokens.text.muted,
    '&:hover': {
      color: tokens.text.primary,
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  removeBtn: {
    padding: '4px',
    marginLeft: '4px',
    color: tokens.text.muted,
    '&:hover': {
      color: tokens.text.primary,
      backgroundColor: 'rgba(255, 80, 80, 0.25)',
    },
  },
  actionIcon: {
    fontSize: 16,
  },
  divider: {
    borderColor: tokens.accordion.border,
    marginY: '4px !important',
  },
})

export default MobileSceneSelect
